// M22 colour-range mask maths (pure, unit-tested; the GPU pass PICK_MASK in engine/shaders/pickPasses.ts
// computes exactly this per pixel). A pixel belongs to the mask by its distance to the sampled colours in
// OkLCh: hue (degrees, around the circle), chroma and lightness, each scaled by a tolerance that grows with
// Range; Softness widens the band where the weight falls from 1 to 0. Hue only counts where both colours
// have some chroma (a grey has no reliable hue), so a red sample never picks the white plate.
import { linearSrgbToOklab, toLch } from '../color/oklab';
import { srgbToLinear } from '../color/transfer';
import type { ColourPreset } from '../model/types';

export type Lch = [number, number, number];

/** Tolerances for a Range of 0..100: hue degrees, chroma and lightness (OkLCh units). */
export function tolerances(range: number): { h: number; c: number; l: number } {
  const r = Math.min(100, Math.max(0, range)) / 100;
  return { h: 6 + 54 * r, c: 0.03 + 0.12 * r, l: 0.06 + 0.24 * r };
}

/** The soft edge for a Softness of 0..100: full weight up to `inner`, none from `outer` (in tolerance units). */
export function softEdge(softness: number): { inner: number; outer: number } {
  const s = 0.05 + (0.75 * Math.min(100, Math.max(0, softness))) / 100;
  return { inner: Math.max(0.02, 1 - s), outer: 1 + s };
}

const smooth = (e0: number, e1: number, x: number) => {
  const t = Math.min(1, Math.max(0, (x - e0) / Math.max(e1 - e0, 1e-6)));
  return t * t * (3 - 2 * t);
};

/** Hue difference in degrees, 0..180, across 0/360. */
export const hueDelta = (a: number, b: number) => {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
};

/** Normalised distance of `px` from one sample (1 = the edge of the range). */
export function colourDistance(px: Lch, s: Lch, range: number): number {
  const t = tolerances(range);
  const k = Math.min(1, Math.max(0, Math.min(px[1], s[1]) / 0.04));
  const dh = (hueDelta(px[2], s[2]) * k) / t.h;
  const dc = Math.abs(px[1] - s[1]) / t.c;
  const dl = Math.abs(px[0] - s[0]) / t.l;
  return Math.sqrt(dh * dh + dc * dc + dl * dl);
}

/** Mask weight 0..1 of a pixel: the best of the samples. */
export function colourWeight(px: Lch, samples: Lch[], range: number, softness: number): number {
  if (!samples.length) return 0;
  const e = softEdge(softness);
  let w = 0;
  for (const s of samples) w = Math.max(w, 1 - smooth(e.inner, e.outer, colourDistance(px, s, range)));
  return w;
}

/** OkLCh of an sRGB-encoded 0..255 colour (presets are written as everyday colours). */
export function lchOfSrgb8(r: number, g: number, b: number): Lch {
  return toLch(
    linearSrgbToOklab([srgbToLinear(r / 255), srgbToLinear(g / 255), srgbToLinear(b / 255)]),
  ) as Lch;
}

/**
 * Quick presets [Tomate, Lechuga / hierbas, Queso, Carne, Pan]: a few reference colours of the ingredient
 * (lit, mid and shadow) and the Range / Softness that suit them. They work with tapped samples too.
 */
export const COLOUR_PRESETS: Record<
  ColourPreset,
  { rgb: [number, number, number][]; range: number; softness: number }
> = {
  tomato: {
    rgb: [
      [200, 35, 30],
      [228, 72, 52],
      [150, 22, 20],
    ],
    range: 35,
    softness: 50,
  },
  greens: {
    rgb: [
      [125, 190, 60],
      [80, 150, 45],
      [45, 105, 35],
      [170, 215, 110],
    ],
    range: 35,
    softness: 50,
  },
  cheese: {
    rgb: [
      [245, 190, 40],
      [240, 160, 30],
      [250, 215, 110],
    ],
    range: 30,
    softness: 50,
  },
  meat: {
    rgb: [
      [110, 62, 38],
      [150, 85, 48],
      [72, 42, 30],
    ],
    range: 30,
    softness: 50,
  },
  bread: {
    rgb: [
      [215, 150, 80],
      [236, 192, 125],
      [182, 112, 52],
    ],
    range: 30,
    softness: 50,
  },
};

export const presetLch = (p: ColourPreset): Lch[] =>
  COLOUR_PRESETS[p].rgb.map(([r, g, b]) => lchOfSrgb8(r, g, b));

/** Most samples a colour mask keeps (tapped + preset); the GPU pass reads this many. */
export const MAX_COLOUR_SAMPLES = 8;
